import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import firebaseService from '@/services/FirebaseService';
import { OneTimerTask, CreateOneTimerTaskRequest, UpdateOneTimerTaskRequest } from '../types';

// Query keys
export const QUERY_KEYS = {
  tasks: ['oneTimers', 'tasks'] as const,
} as const;

// Utility to strip undefined values so Firebase doesn't reject the payload
function removeUndefinedValues<T extends Record<string, any>>(obj: T): Partial<T> {
  const cleaned: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key as keyof T] = value;
    }
  }
  return cleaned;
}

/**
 * Fetch all One-Timer tasks for the current user
 */
const getTasks = async (): Promise<OneTimerTask[]> => {
  return await firebaseService.getCollection<OneTimerTask>('oneTimers');
};

/**
 * Create a new One-Timer task
 */
const createTask = async (request: CreateOneTimerTaskRequest): Promise<OneTimerTask> => {
  const id = Date.now().toString();
  const now = new Date().toISOString();

  // Get max sort order to put new task at the end
  const existingTasks = await firebaseService.getCollection<OneTimerTask>('oneTimers');
  const maxOrder = existingTasks.reduce((max, task) => Math.max(max, task.sortOrder || 0), 0);

  const task: OneTimerTask = removeUndefinedValues({
    id,
    title: request.title.trim(),
    description: request.description?.trim(),
    deadline: request.deadline,
    isDone: false,
    createdAt: now,
    sortOrder: maxOrder + 1,
  }) as OneTimerTask;

  await firebaseService.setDocument('oneTimers', id, task);
  return task;
};

/**
 * Update task order for multiple tasks
 */
const updateTaskOrder = async (taskUpdates: { id: string; sortOrder: number }[]): Promise<void> => {
  // Update each task's sortOrder in Firebase
  const updatePromises = taskUpdates.map(({ id, sortOrder }) =>
    firebaseService.updateDocument('oneTimers', id, { sortOrder })
  );
  await Promise.all(updatePromises);
};

/**
 * Mark a task as done
 */
const markTaskDone = async (taskId: string): Promise<OneTimerTask> => {
  const now = new Date().toISOString();
  await firebaseService.updateDocument('oneTimers', taskId, {
    isDone: true,
    completedAt: now,
  });
  return await firebaseService.getDocument<OneTimerTask>('oneTimers', taskId, {} as OneTimerTask);
};

/**
 * Update an existing task
 */
const updateTask = async (id: string, updates: UpdateOneTimerTaskRequest): Promise<OneTimerTask> => {
  // For updates, we want to include all provided fields, even if they're empty strings
  // This allows clearing optional fields like description
  const preparedUpdates = {
    ...updates,
    title: updates.title?.trim(),
    description: updates.description?.trim(), // Keep empty strings to allow clearing
  };
  
  // Only remove undefined values (but keep empty strings)
  const cleanUpdates = removeUndefinedValues(preparedUpdates);
  
  await firebaseService.updateDocument('oneTimers', id, cleanUpdates);
  return await firebaseService.getDocument<OneTimerTask>('oneTimers', id, {} as OneTimerTask);
};

/**
 * Delete a task
 */
const deleteTask = async (taskId: string): Promise<void> => {
  await firebaseService.deleteDocument('oneTimers', taskId);
};

/**
 * Undo a completed task (mark as not done)
 */
const undoTask = async (taskId: string): Promise<OneTimerTask> => {
  await firebaseService.updateDocument('oneTimers', taskId, {
    isDone: false,
    completedAt: null,
  });
  return await firebaseService.getDocument<OneTimerTask>('oneTimers', taskId, {} as OneTimerTask);
};

// ----------------- React Query hooks -----------------

export const useGetOneTimerTasks = () => {
  return useQuery({
    queryKey: QUERY_KEYS.tasks,
    queryFn: getTasks,
  });
};

export const useCreateOneTimerTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
};

export const useMarkTaskDone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markTaskDone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
};

export const useUpdateTaskOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTaskOrder,
    onMutate: async (taskUpdates) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.tasks });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<OneTimerTask[]>(QUERY_KEYS.tasks);

      // Optimistically update the cache
      if (previousTasks) {
        const updatedTasks = previousTasks.map(task => {
          const update = taskUpdates.find(u => u.id === task.id);
          return update ? { ...task, sortOrder: update.sortOrder } : task;
        });
        queryClient.setQueryData(QUERY_KEYS.tasks, updatedTasks);
      }

      // Return context object with the snapshotted value
      return { previousTasks };
    },
    onError: (err, taskUpdates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTasks) {
        queryClient.setQueryData(QUERY_KEYS.tasks, context.previousTasks);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateOneTimerTaskRequest }) => 
      updateTask(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
};

export const useUndoTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: undoTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}; 